<template>
  <font-awesome-icon :icon="icon" :spin="spin" fixed-width :class="iconClasses"></font-awesome-icon>
  <span v-if="!hideText" class="ml-1.5" :class="textClasses"><slot>{{ defaultText }}</slot></span>
</template>

<script setup lang="ts">
import { computed } from 'vue';

type FStatusVariant = 'pending' | 'action-required' | 'success' | 'failed' | 'cancelled' | 'cancelling' | 'running' | 'completed' | 'expired' | null;
type FStatusSize = 'default' | 'large';

interface FFailedProps {
  variant?: FStatusVariant;
  hideText?: boolean;
  size?: FStatusSize;
}

const props = withDefaults(defineProps<FFailedProps>(), {
  variant: 'pending',
  hideText: false,
  size: 'default',
});

const spin = computed(() => props.variant === 'running');

const icon = computed(() => {
  switch (props.variant) {
    case 'success':
    case 'completed':
      return ['far', 'check-circle'];
    case 'failed':
      return ['far', 'times-circle'];
    case 'action-required':
      return ['far', 'flag'];
    case 'cancelled':
      return ['far', 'ban'];
    case 'running':
      return ['fa', 'circle-notch'];
    case 'cancelling':
      return ['fa', 'circle-notch'];
    case 'pending':
      return ['far', 'clock'];
    case 'expired':
      return ['far', 'hourglass'];
  }

  return ['far', 'circle'];
});

const iconClasses = computed(() => {
  const classes: Array<string> = [ ];

  switch (props.size) {
    case 'large':
      classes.push('fa-lg');
      break;
  }
  
  switch (props.variant) {
    case 'success':
    case 'completed':
      classes.push('text-green-500');
      break;
    case 'failed':
      classes.push('text-red-500');
      break;
    case 'action-required':
      classes.push('text-yellow-500');
      break;
    case 'running':
      classes.push('text-link');
      break;
    case 'cancelling':
      classes.push('text-link');
      break;
    case 'pending':
      classes.push('text-gray-300');
      break;
    default:
      classes.push('text-default');
      break;
  }

  return classes;
});

const textClasses = computed(() => {
  switch (props.variant) {
    case 'success':
    case 'completed':
      return ['text-green-500'];
    case 'failed':
      return ['text-red-500'];
    case 'action-required':
      return ['text-yellow-500'];
    case 'running':
      return ['text-blue-500'];
    case 'cancelling':
      return ['text-blue-500'];
    case 'pending':
      return ['text-gray-300'];
  }

  return ['text-default'];
});

const defaultText = computed(() => {
  switch (props.variant) {
    case 'success':
      return 'Success';
    case 'failed':
      return  'Failed';
    case 'action-required':
      return 'Action required';
    case 'pending':
      return 'Pending';
    case 'cancelled':
      return 'Cancelled';
    case 'running':
      return 'Running';
    case 'cancelling':
      return 'Cancelling';
    case 'completed':
      return 'Completed';
    case 'expired':
      return 'Expired';
  }

  return 'Unknown';
});
</script>